/* ============================================================================
   legal-lightbox.js - Terms / Privacy / Asset usage / FAQ in an overlay panel
   ============================================================================ */

import { ASSET_VERSION } from "./asset-version.js?v=20261007m";
import { fillIcons } from "./fill-icons.js?v=20261007m";

const LEGAL_FILES = {
  terms: "terms.html",
  privacy: "privacy.html",
  "asset-usage": "asset-usage.html",
  faq: "faq.html",
};

/** Resolved payloads. HTML/seed is stored as soon as it arrives so clicks never wait on CMS. */
const ready = new Map();
const inflight = new Map();
let wired = false;
let loading = false;

function parseLegalHref(href) {
  try {
    const url = new URL(href, window.location.href);
    const match = url.pathname.match(/\/(terms|privacy|asset-usage|faq)\.html$/i);
    if (!match) return null;
    const id = match[1].toLowerCase();
    if (id === "asset-usage") return { id: "asset-usage", hash: url.hash };
    return { id, hash: url.hash };
  } catch {
    return null;
  }
}

function remember(id, payload) {
  if (payload) ready.set(id, payload);
}

function faqPayloadFromItems(items, renderFaqLayout) {
  const wrap = document.createElement("div");
  wrap.className = "faq-layout";
  renderFaqLayout(wrap, items);
  return {
    title: "FAQ",
    bodyHtml: `<h1>FAQ</h1><p class="muted">Credits, Asset Jar, outfits, account stuff.</p>${wrap.outerHTML}`,
  };
}

async function loadHtmlPayload(id) {
  const file = LEGAL_FILES[id];
  if (!file) throw new Error("Unknown legal document");
  const res = await fetch(`${file}?v=${ASSET_VERSION}`, { credentials: "same-origin" });
  if (!res.ok) throw new Error("Could not load document");
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const main = doc.querySelector(".legal-doc");
  if (!main) throw new Error("Missing legal content");
  return {
    title: main.querySelector("h1")?.textContent?.trim() || "Legal",
    bodyHtml: main.innerHTML,
  };
}

async function startLegalLoad(id) {
  if (inflight.has(id)) return inflight.get(id);

  const work = (async () => {
    if (id === "faq") {
      const copy = await import(`./site-copy.js?v=${ASSET_VERSION}`);
      const seed = faqPayloadFromItems(copy.mergeFaqItems("portal", [], []), copy.renderFaqLayout);
      remember(id, seed);
      try {
        const cms = await copy.fetchFaqCms({ surface: "portal" });
        remember(id, faqPayloadFromItems(copy.mergeFaqItems("portal", cms.items, cms.hidden_ids), copy.renderFaqLayout));
      } catch (_) {}
      return ready.get(id);
    }

    // Static HTML first (fast hosting). CMS can replace later without blocking open.
    const htmlP = loadHtmlPayload(id).then((payload) => {
      remember(id, payload);
      return payload;
    });
    const copyP = import(`./site-copy.js?v=${ASSET_VERSION}`);

    await htmlP;

    void copyP
      .then((copy) =>
        copy.fetchLegalDoc(id).then((cmsDoc) => {
          if (!cmsDoc?.body_html) return null;
          remember(id, {
            title: cmsDoc.title || "Legal",
            bodyHtml: copy.sanitizeLegalHtml(cmsDoc.body_html),
          });
          return ready.get(id);
        })
      )
      .catch(() => null);

    return ready.get(id);
  })();

  inflight.set(id, work);
  work.catch(() => {
    inflight.delete(id);
  });
  return work;
}

async function fetchLegalDoc(id) {
  if (ready.has(id)) return ready.get(id);
  await startLegalLoad(id);
  if (ready.has(id)) return ready.get(id);
  throw new Error("Could not load document");
}

/** Warm FAQ / terms / privacy / asset usage so landing overlays open without a wait. */
export function prefetchLegalDocs(ids = Object.keys(LEGAL_FILES)) {
  for (const id of ids) {
    if (!LEGAL_FILES[id]) continue;
    void startLegalLoad(id).catch(() => {});
  }
}

function ensureLightbox() {
  let el = document.getElementById("legal-lightbox");
  if (el) return el;

  el = document.createElement("div");
  el.id = "legal-lightbox";
  el.className = "legal-lightbox";
  el.hidden = true;
  el.setAttribute("aria-hidden", "true");
  el.innerHTML = `
    <div class="legal-lightbox__frame">
      <div class="legal-lightbox__panel" role="dialog" aria-modal="true" aria-labelledby="legal-lightbox-title">
        <button type="button" class="request-modal__close legal-lightbox__close" aria-label="Close">
          <span data-icon="close"></span>
        </button>
        <div class="legal-lightbox__scroll">
          <div class="legal-lightbox__body legal-doc" id="legal-lightbox-body"></div>
        </div>
      </div>
    </div>`;
  document.body.appendChild(el);
  fillIcons(el);

  el.addEventListener("click", (e) => {
    if (e.target === el) closeLegalLightbox();
  });
  el.querySelector(".legal-lightbox__close")?.addEventListener("click", () => closeLegalLightbox());

  if (document.documentElement.dataset.legalLightboxEscapeWired !== "1") {
    document.documentElement.dataset.legalLightboxEscapeWired = "1";
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      const lb = document.getElementById("legal-lightbox");
      if (lb?.classList.contains("is-open")) closeLegalLightbox();
    });
  }

  return el;
}

export function closeLegalLightbox() {
  const el = document.getElementById("legal-lightbox");
  if (!el) return;
  el.classList.remove("is-open");
  el.hidden = true;
  el.setAttribute("aria-hidden", "true");
  document.body.classList.remove("legal-lightbox-open");
  el.querySelector("#legal-lightbox-body")?.replaceChildren();
}

export async function openLegalLightbox(id, { hash = "" } = {}) {
  const key = String(id || "").trim();
  if (!LEGAL_FILES[key]) return;

  const el = ensureLightbox();
  const body = el.querySelector("#legal-lightbox-body");
  if (!body) return;

  loading = true;
  const paint = (doc) => {
    body.innerHTML = doc.bodyHtml;
    const title = body.querySelector("h1");
    if (title) title.id = "legal-lightbox-title";
    if (hash) {
      const target = body.querySelector(hash);
      if (target) target.scrollIntoView({ block: "start" });
    } else {
      el.querySelector(".legal-lightbox__scroll")?.scrollTo(0, 0);
    }
  };

  const cached = ready.get(key);
  if (cached) paint(cached);
  else body.innerHTML = `<p class="muted legal-lightbox__loading">Loading…</p>`;

  el.classList.add("is-open");
  el.hidden = false;
  el.setAttribute("aria-hidden", "false");
  document.body.classList.add("legal-lightbox-open");
  el.querySelector(".legal-lightbox__close")?.focus();

  try {
    const doc = await fetchLegalDoc(key);
    // Prefer freshest payload (CMS may have replaced seed/HTML after open).
    paint(ready.get(key) || doc);
  } catch {
    if (!ready.has(key)) {
      body.innerHTML = `<p class="muted">Could not load this document. <a href="${LEGAL_FILES[key]}">Open the page</a> instead.</p>`;
    }
  } finally {
    loading = false;
  }
}

function onLegalLinkClick(e) {
  if (loading) return;
  const anchor = e.target.closest("a[href]");
  if (!anchor) return;
  if (e.defaultPrevented) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

  // Keep sidebar / primary nav FAQ as a full page.
  if (anchor.matches(".nav__item") || anchor.closest(".sidebar, .nav")) return;

  const rawHref = anchor.getAttribute("href") || "";
  const parsed = parseLegalHref(rawHref);
  if (!parsed) return;

  // Same-page hash jumps (FAQ section chips, legal TOC) should scroll, not open a lightbox.
  try {
    const url = new URL(rawHref, window.location.href);
    const curFile = (window.location.pathname.split("/").pop() || "").toLowerCase();
    const targetFile = (url.pathname.split("/").pop() || "").toLowerCase();
    if (curFile && curFile === targetFile) return;
  } catch {
    return;
  }

  e.preventDefault();
  openLegalLightbox(parsed.id, { hash: parsed.hash });
}

/** Intercept footer and in-app links to legal HTML pages. Standalone pages remain for direct URLs / SEO. */
export function wireLegalLinks() {
  if (wired) return;
  wired = true;
  document.addEventListener("click", onLegalLinkClick, true);
}
